import { FilterDropdown } from '@/shared/components/FilterDropdown'
import { TaskDetailDialog } from '@/shared/components/legal-documents/TaskDetailDialog'
import { DocumentDetail } from '@/shared/components/projects/DocumentDetail'
import { BodyTable } from '@/shared/components/projects/project-profile/BodyTable'
import { useProjectProfileColumns } from '@/shared/components/projects/project-profile/columns'
import { createFileRoute } from '@tanstack/react-router'
import { ExpandedState, getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table'
import { Button } from '@workspace/ui/components/Button'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { Table } from '@workspace/ui/components/Table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ShareDialog } from '@/shared/components/dialogs/ShareDialog'
import { AddDocumentDialog } from '@/shared/components/projects/AddDocumentDialog'
import { BatchAddDocumentDialog } from '@/shared/components/projects/BatchAddDocumentDialog'
import AddNew from '@/shared/components/projects/project-profile/AddNew'
import { HeadTable } from '@/shared/components/projects/project-profile/HeadTable'
import ShowOrganizationDocuments from '@/shared/components/projects/project-profile/ShowOrganizationDocuments'
import { getSecurityOptions, getStatusOptions } from '@/shared/constants/project-profile'
import { useBulkDeleteDocuments } from '@/shared/hooks/documents/useDelete'
import { useBulkDeleteFolders } from '@/shared/hooks/folders/useDeleteFolder'
import { useLazyLoad } from '@/shared/hooks/folders/useLazyLoad'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useExportExcel } from '@/shared/hooks/folders/useExportExcel'
import { Task, SecurityLevel } from '@/shared/lib/api'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { NormalizeFolderStructure } from '@/shared/types/folder-structure'
import { treeBuilder } from '@/shared/utils/build-tree'
import { confirm } from '@workspace/ui/components/ConfirmDialog'
import { cn } from '@workspace/ui/lib/utils'
import AddBasicDocument from '@/shared/components/documents/AddBasicDocument'
import AddFolderDialog from '@/shared/components/projects/AddFolderDialog'
import { useNProgress } from '@workspace/ui/components/NProgress'
import { useCan } from '@/shared/lib/authorization'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/shared/lib/api'
import { useProjectPermissions } from '@/shared/hooks/projects/useProjectPermissions'
import { useUser } from '@/shared/store/auth.store'
import { ADMIN_ROLE_NAME } from '@/shared/constants'
import { PreviewSearchItem } from '@/shared/components/previewSearchItem'
import { useGetProjectOverview } from '@/shared/hooks/projects/useGetProjectOverview'
import { LabelCustomizationDialog } from '@/shared/components/dialogs/LabelCustomizationDialog'

export const Route = createFileRoute('/projects/$id/_pathlessLayout/project-profile')({
    component: ProjectProfile,
    beforeLoad: requirePermissionBeforeLoad('view', 'project'),
})

function canViewItem(
    item: any,
    isManager: boolean,
    isAdmin: boolean,
    currentUserId: string,
    sharedResourceIds: Set<string>,
    isParentResponsible = false,
): boolean {
    if (isAdmin || isManager) return true

    if (item.classificationLevel === SecurityLevel.NORMAL) return true

    const responsibleId = item.responsibleUserId || item.responsibleUser?.id
    if (responsibleId && responsibleId === currentUserId) return true

    if (isParentResponsible) return true

    return sharedResourceIds.has(item.id)
}

function filterTreeByPermission(
    items: any[],
    isManager: boolean,
    isAdmin: boolean,
    currentUserId: string,
    sharedResourceIds: Set<string>,
    isParentResponsible = false,
): any[] {
    return items
        .map(item => {
            const isResponsible =
                isParentResponsible ||
                (item.responsibleUserId && item.responsibleUserId === currentUserId) ||
                (item.responsibleUser?.id && item.responsibleUser.id === currentUserId)

            let filteredChildren: any[] = []
            if (item.children && item.children.length > 0) {
                filteredChildren = filterTreeByPermission(
                    item.children,
                    isManager,
                    isAdmin,
                    currentUserId,
                    sharedResourceIds,
                    isResponsible,
                )
            }

            const hasViewableChildren = filteredChildren.length > 0
            const canViewSelf = canViewItem(item, isManager, isAdmin, currentUserId, sharedResourceIds, isResponsible)

            if (canViewSelf || hasViewableChildren) {
                return {
                    ...item,
                    children: filteredChildren,
                }
            }

            return null
        })
        .filter(Boolean)
}

function ProjectProfile() {
    const { id } = Route.useParams() as { id: string }
    const { t } = useTranslation()
    const { data: overviewData } = useGetProjectOverview(id)
    const projectDetail = overviewData?.project
    const { mutate: exportExcel, isPending: isExporting } = useExportExcel({
        name: projectDetail?.name || 'project',
        code: projectDetail?.code || '',
    })
    const deleteFolderMutation = useBulkDeleteFolders()
    const deleteDocumentMutation = useBulkDeleteDocuments()
    const canActions = useCan('operate', 'project')
    const user = useUser()
    const isAdmin = user?.role?.name === ADMIN_ROLE_NAME
    const currentUserId = user?.id || ''

    const projectPerm = useProjectPermissions(id)
    const hasQueried = projectPerm.hasQueriedApi
    const isManager = isAdmin || (hasQueried ? projectPerm.canManage : false)

    // Effective actions for project-scoped operations:
    // - Admins and managers can always act
    // - Until project permissions are queried, disallow actions (conservative)
    // - Otherwise use project-specific flags (modify/download/manage)
    const effectiveCanActions = useMemo(() => {
        if (isAdmin || isManager) return true
        if (!projectPerm.hasQueriedApi) return false
        return projectPerm.canInput || projectPerm.canDownload || projectPerm.canManage
    }, [isAdmin, isManager, projectPerm])

    // Separate permissions for specific actions
    const canAddNew = useMemo(() => {
        if (isAdmin || isManager) return true
        if (!projectPerm.hasQueriedApi) return false
        return projectPerm.canInput
    }, [isAdmin, isManager, projectPerm])

    const canOrganizeDocuments = useMemo(() => {
        if (isAdmin || isManager) return true
        if (!projectPerm.hasQueriedApi) return false
        return projectPerm.canManage
    }, [isAdmin, isManager, projectPerm])

    const [securityFilter, setSecurityFilter] = useState<string[]>([])
    const [statusFilter, setStatusFilter] = useState<string[]>([])
    const [searchQuery, setSearchQuery] = useState('')
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
    const hasSelectedFolder = useMemo(() => {
        return Object.keys(rowSelection).some(key => key.endsWith('_folder'))
    }, [rowSelection])

    const canBulkDelete = useMemo(() => {
        if (isAdmin || isManager) return true
        if (!projectPerm.hasQueriedApi) return false
        if (hasSelectedFolder) return false
        return projectPerm.canInput
    }, [isAdmin, isManager, hasSelectedFolder, projectPerm])

    const [selectedDocument, setSelectedDocument] = useState<NormalizeFolderStructure | null>(null)
    const [editDocumentId, setEditDocumentId] = useState<{ id: string; type: 'edit' | 'upload' } | undefined>(undefined)
    const [addEditFolderOpen, setAddEditFolderOpen] = useState<{
        id?: string
        data?: any
        type?: 'add' | 'edit'
    }>({})
    const [addDocumentByFolder, setAddDocumentByFolder] = useState<string>('')
    const [shareDocument, setShareDocument] = useState<NormalizeFolderStructure | null>(null)
    const [taskDetailId, setTaskDetailId] = useState<string | null>(null)
    const [expanded, setExpanded] = useState<ExpandedState>({})
    const [nodeToPrint, setNodeToPrint] = useState<NormalizeFolderStructure | null>(null)

    const debouncedSearch = useDebounce(searchQuery, 500)
    const trimmedSearch = debouncedSearch?.trim() ?? ''
    const isSearchActive = trimmedSearch.length >= 2
    const effectiveSearch = isSearchActive ? trimmedSearch : undefined

    // Get users from project permissions only
    const { data: projectPermissions } = useQuery({
        queryKey: ['project-permissions', id],
        queryFn: () => api.projectPermission.list({ projectId: id, page: 1, take: 100 }),
    })

    // Extract unique users from permissions
    const userRes = useMemo(() => {
        if (!projectPermissions?.data)
            return {
                data: [],
                meta: { itemCount: 0, page: 1, take: 100, pageCount: 0, hasPreviousPage: false, hasNextPage: false },
            }

        const uniqueUsers = new Map()
        projectPermissions.data.forEach(permission => {
            if (permission.user && permission.userId) {
                uniqueUsers.set(permission.userId, permission.user)
            }
        })

        return {
            data: Array.from(uniqueUsers.values()),
            meta: {
                itemCount: uniqueUsers.size,
                page: 1,
                take: 100,
                pageCount: 1,
                hasPreviousPage: false,
                hasNextPage: false,
            },
        }
    }, [projectPermissions])

    const { data, isLoading, isFetching } = useLazyLoad({
        projectId: id,
        search: effectiveSearch,
        depthChildren: isSearchActive ? 0 : 2,
        classificationLevel: securityFilter.join(',') || undefined,
        completionStatus: statusFilter.join(',') || undefined,
    })

    const restrictedResourceIds = useMemo(() => {
        if (!data?.items) return { folders: [], documents: [] }

        const folders: string[] = []
        const documents: string[] = []

        const extractRestricted = (items: any[]) => {
            items.forEach((item: any) => {
                // Check if item is a folder (has folderType or children)
                const isFolder = item.folderType || item.children !== undefined

                if (item.classificationLevel && item.classificationLevel !== SecurityLevel.NORMAL) {
                    if (isFolder) {
                        folders.push(item.id)
                    } else {
                        documents.push(item.id)
                    }
                }

                // Recursively check children
                if (item.children && Array.isArray(item.children)) {
                    extractRestricted(item.children)
                }

                // Check nested documents
                if (item.documents && Array.isArray(item.documents)) {
                    item.documents.forEach((doc: any) => {
                        if (doc.classificationLevel && doc.classificationLevel !== SecurityLevel.NORMAL) {
                            documents.push(doc.id)
                        }
                    })
                }
            })
        }

        extractRestricted(data.items as any[])

        return { folders, documents }
    }, [data?.items])

    // Fetch shares cho tất cả restricted resources
    const { data: userResourceShares, isLoading: isLoadingShares } = useQuery({
        queryKey: ['restricted-resource-shares', restrictedResourceIds, currentUserId],
        queryFn: async () => {
            const { folders, documents } = restrictedResourceIds

            if (isManager || isAdmin) return { data: [] }

            if (folders.length === 0 && documents.length === 0) {
                return { data: [] }
            }

            try {
                // Fetch shares cho từng resource
                const folderPromises = folders.map(folderId =>
                    api.resourceShare
                        .list({
                            page: 1,
                            take: 100,
                            resourceType: 'folder',
                            resourceId: folderId,
                        })
                        .catch(err => {
                            console.error('Failed to fetch shares for folder:', folderId, err)
                            return { data: [] }
                        }),
                )

                const documentPromises = documents.map(docId =>
                    api.resourceShare
                        .list({
                            page: 1,
                            take: 100,
                            resourceType: 'document',
                            resourceId: docId,
                        })
                        .catch(err => {
                            console.error('Failed to fetch shares for document:', docId, err)
                            return { data: [] }
                        }),
                )

                const results = await Promise.all([...folderPromises, ...documentPromises])

                // Flatten và filter cho user hiện tại
                const allShares = results
                    .flatMap(result => result.data || [])
                    .filter((share: any) => share.userId === currentUserId)

                return { data: allShares }
            } catch (error) {
                console.error('❌ Failed to fetch resource shares:', error)
                return { data: [] }
            }
        },
        enabled:
            !!currentUserId &&
            !isAdmin &&
            !isManager &&
            (restrictedResourceIds.folders.length > 0 || restrictedResourceIds.documents.length > 0),
        staleTime: 5 * 60 * 1000, // Cache 5 phút
    })

    // Tạo Set các resource IDs được share
    const sharedResourceIds = useMemo(() => {
        if (isManager || isAdmin) return new Set<string>() // Manager/Admin không cần check

        const ids = new Set<string>()

        // Extract từ userResourceShares
        userResourceShares?.data?.forEach((share: any) => {
            if (share.resourceId) {
                ids.add(share.resourceId)
            }
        })

        return ids
    }, [userResourceShares, isManager, isAdmin])

    useNProgress({ isFetching: isLoading || isLoadingShares })

    // Memoize filter options
    const securityOptions = useMemo(() => getSecurityOptions(t), [t])
    const statusOptions = useMemo(() => getStatusOptions(t), [t])

    // Memoize selected count
    const selectedCount = useMemo(() => Object.keys(rowSelection)?.length, [rowSelection])

    // Callback handlers
    const handleViewDocument = useCallback((document: NormalizeFolderStructure) => {
        setSelectedDocument(document)
    }, [])

    const handleAddEditFolder = useCallback((id?: string, data?: NormalizeFolderStructure, type?: 'add' | 'edit') => {
        setAddEditFolderOpen({ id, data, type })
    }, [])

    const handleEditDocument = useCallback((id: string, type: 'edit' | 'upload') => {
        setEditDocumentId({ id, type })
    }, [])

    const handleShareDocument = useCallback((document: NormalizeFolderStructure) => {
        setShareDocument(document)
    }, [])

    const handleAddDocumentByFolder = useCallback((id: string) => {
        setAddDocumentByFolder(id)
    }, [])

    const handleExportExcel = useCallback(() => {
        exportExcel({
            projectId: id,
            search: debouncedSearch || undefined,
            classificationLevel: securityFilter.join(',') || undefined,
            completionStatus: statusFilter.join(',') || undefined,
        })
    }, [exportExcel, id, debouncedSearch, securityFilter, statusFilter])

    const handleViewTaskDetail = useCallback((task: Task) => {
        setTaskDetailId(task.id)
    }, [])

    const handleBulkDelete = useCallback(async () => {
        confirm({
            title: t('projects.projectProfile.table.deleteDialog.bulkDeleteTitle'),
            description: t('projects.projectProfile.table.deleteDialog.bulkDeleteDescription', {
                count: selectedCount,
            }),
            action: {
                label: t('common.confirmDialog.confirm'),
                onClick: async () => {
                    const keyArr = Object.keys(rowSelection)
                    const { folderIds, documentIds } = keyArr.reduce(
                        (acc, key) => {
                            const [id, type] = key.split('_')
                            if (id && type === 'folder') {
                                acc.folderIds.push(id)
                            } else if (id && type === 'document') {
                                acc.documentIds.push(id)
                            }
                            return acc
                        },
                        { folderIds: [] as string[], documentIds: [] as string[] },
                    )

                    try {
                        const promises = []
                        if (folderIds.length > 0) {
                            promises.push(deleteFolderMutation.mutateAsync({ ids: folderIds }))
                        }
                        if (documentIds.length > 0) {
                            promises.push(deleteDocumentMutation.mutateAsync({ ids: documentIds }))
                        }

                        await Promise.all(promises)
                        setRowSelection({})
                    } catch (error) {
                        // Error is already handled by useMutation onError via handleApiError
                        console.error('Bulk delete failed:', error)
                    }
                },
            },
            cancel: {
                label: t('common.confirmDialog.cancel'),
            },
            variant: 'destructive',
        })
    }, [t, selectedCount, rowSelection, deleteFolderMutation, deleteDocumentMutation])

    const handlePrintLabel = useCallback((document: NormalizeFolderStructure) => {
        setNodeToPrint(document)
    }, [])

    // Get columns from hook
    const columns = useProjectProfileColumns(
        handleViewDocument,
        handleEditDocument,
        handleAddEditFolder,
        handleShareDocument,
        handleAddDocumentByFolder,
        handleViewTaskDetail,
        handlePrintLabel,
        userRes,
        sharedResourceIds,
        isManager,
        isAdmin,
    )

    // QUAN TRỌNG: Filter data theo quyền trước khi build tree
    // CHỜ shares load xong mới filter
    const filteredData = useMemo(() => {
        if (!data?.items) return []

        // Manager/Admin thấy hết
        if (isManager) return data.items

        const itemMap = new Map<string, any>()
        const directViewableIds = new Set<string>()

        const traverse = (items: any[], isParentResponsible = false) => {
            items.forEach((item: any) => {
                itemMap.set(item.id, item)

                const respId = item.responsibleUserId || item.responsibleUser?.id
                const isResponsible = isParentResponsible || (respId && respId === currentUserId)

                if (
                    item.classificationLevel === SecurityLevel.NORMAL ||
                    isResponsible ||
                    sharedResourceIds.has(item.id)
                ) {
                    directViewableIds.add(item.id)
                }

                if (item.children && Array.isArray(item.children) && item.children.length > 0) {
                    traverse(item.children, isResponsible)
                }

                if (item.documents && Array.isArray(item.documents) && item.documents.length > 0) {
                    traverse(item.documents, isResponsible)
                }
            })
        }

        traverse(data.items)

        const viewableIds = new Set<string>(directViewableIds)
        directViewableIds.forEach(id => {
            let curr = itemMap.get(id)
            const getParentId = (item: any) => item?.parentId || item?.folderId
            let pId = getParentId(curr)

            while (curr && pId) {
                viewableIds.add(pId)
                curr = itemMap.get(pId)
                pId = getParentId(curr)
            }
        })

        const filterTree = (items: any[]): any[] => {
            return items
                .filter((item: any) => viewableIds.has(item.id))
                .map((item: any) => {
                    if (item.children && Array.isArray(item.children)) {
                        return {
                            ...item,
                            children: filterTree(item.children),
                        }
                    }
                    return item
                })
        }

        return filterTree(data.items)
    }, [data?.items, isManager, isAdmin, currentUserId, sharedResourceIds, isLoadingShares])

    const folderStructure = useMemo(() => {
        if (!filteredData.length) return []

        const tree = treeBuilder(filteredData)

        // Filter tree recursively để đảm bảo children cũng được filter
        return filterTreeByPermission(tree, isManager, isAdmin, currentUserId, sharedResourceIds)
    }, [filteredData, isManager, isAdmin, currentUserId, sharedResourceIds])

    useEffect(() => {
        if (isSearchActive && folderStructure && folderStructure.length > 0) {
            const newExpanded: ExpandedState = {}
            const expandAll = (items: any[]) => {
                items.forEach(item => {
                    if (item.type === 'folder') {
                        newExpanded[`${item.id}_folder`] = true
                        if (item.children && item.children.length > 0) {
                            expandAll(item.children)
                        }
                    }
                })
            }
            expandAll(folderStructure)
            setExpanded(newExpanded)
        }
    }, [isSearchActive, folderStructure])

    const table = useReactTable({
        data: folderStructure || [],
        columns,
        state: {
            expanded,
            rowSelection,
        },
        onExpandedChange: setExpanded,
        onRowSelectionChange: setRowSelection,
        getRowId: row => `${row.id}_${row.type}`,
        getSubRows: row => row.children,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        enableRowSelection: true,
    })

    const rowCountMemo = useMemo(() => folderStructure?.length || 0, [folderStructure])

    const bodyTableMemo = useMemo(
        () => <BodyTable table={table} isSearching={isSearchActive} />,
        [table, expanded, folderStructure, rowSelection, isSearchActive],
    )
    const isDeleting = deleteDocumentMutation.isPending || deleteFolderMutation.isPending

    return (
        <>
            <div className="p-4 space-y-4 bg-background border border-border rounded-xl">
                <h1 className="text-xl font-bold">{t('projects.projectProfile.title')}</h1>

                <div className="flex flex-wrap gap-4 justify-between items-center">
                    <div className="flex flex-wrap flex-1 items-center gap-4 w-full">
                        <BsSearchField
                            autoTrim
                            className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                            value={searchQuery}
                            containerClassName="max-lg:w-full"
                            placeholder={`${t('common.search')}...`}
                            onChange={value => setSearchQuery(value)}
                        />

                        <FilterDropdown
                            className={'max-lg:w-full'}
                            containerClassName={'max-lg:flex-1'}
                            placeholder={t('projects.projectProfile.filters.securityLevel')}
                            options={securityOptions}
                            selectedValues={securityFilter}
                            onChange={setSecurityFilter}
                        />

                        <FilterDropdown
                            className={'max-lg:w-full'}
                            containerClassName={'max-lg:flex-1'}
                            placeholder={t('projects.projectProfile.filters.status')}
                            options={statusOptions}
                            selectedValues={statusFilter}
                            onChange={setStatusFilter}
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
                        {canOrganizeDocuments && (
                            <ShowOrganizationDocuments
                                closeExpand={() => setExpanded({})}
                                projectId={id}
                                canActionsOverride={canOrganizeDocuments}
                            />
                        )}

                        {canAddNew && (
                            <AddNew
                                rowCount={rowCountMemo}
                                canActionsOverride={canAddNew}
                                isManager={isManager}
                                projectId={id}
                                regionId={projectDetail?.region?.id}
                                areaId={projectDetail?.area?.id}
                                templateId={projectDetail?.templateId}
                            />
                        )}

                        {effectiveCanActions && (
                            <Button
                                variant="outline"
                                className="gap-2 rounded-lg bg-background"
                                onClick={handleExportExcel}
                                isDisabled={isExporting}
                            >
                                <img src="/file-download.svg" className="w-4 h-4" />
                                {t('projects.projectProfile.toolbar.exportExcel')}
                            </Button>
                        )}
                    </div>
                </div>

                <Table hideBorder className={cn(isDeleting && 'opacity-50 cursor-not-allowed')}>
                    <HeadTable table={table} />
                    {bodyTableMemo}
                </Table>
            </div>

            {selectedCount > 0 && (
                <div className="flex items-center flex-1 gap-3 rounded-lg">
                    <span className="text-sm font-medium text-muted-foreground">
                        {t('common.pagination.selected')}{' '}
                        <span className="font-semibold text-foreground">{selectedCount}</span>{' '}
                        {t('common.pagination.results')}
                    </span>
                    <Button
                        variant="outline"
                        size="default"
                        className="text-sm px-9 bg-background border-border-error-light text-text-error"
                        onClick={handleBulkDelete}
                        isDisabled={!canBulkDelete}
                    >
                        {t('common.actions.delete')}
                    </Button>
                </div>
            )}

            {selectedDocument && (
                <DocumentDetail
                    open={!!selectedDocument}
                    onOpenChange={() => setSelectedDocument(null)}
                    documentId={selectedDocument.id}
                    projectPermProp={projectPerm}
                />
            )}

            {editDocumentId && (
                <AddDocumentDialog
                    open={!!editDocumentId}
                    onOpenChange={() => setEditDocumentId(undefined)}
                    documentId={editDocumentId.id}
                    type={editDocumentId.type}
                    typeSubmit="no-prefetch"
                />
            )}

            {addEditFolderOpen.id && (
                <AddFolderDialog
                    open={!!addEditFolderOpen.id}
                    onOpenChange={() => setAddEditFolderOpen({})}
                    type={addEditFolderOpen.type}
                    folderId={addEditFolderOpen.id}
                    data={addEditFolderOpen.data}
                    typeSubmit="no-prefetch"
                />
            )}

            {shareDocument && (
                <ShareDialog
                    isOpen={!!shareDocument}
                    onOpenChange={() => setShareDocument(null)}
                    documentId={shareDocument.id}
                    documentName={shareDocument.name}
                    resourceType={shareDocument.type === 'folder' ? 'folder' : 'document'}
                    projectId={id}
                />
            )}
            {addDocumentByFolder && (
                <BatchAddDocumentDialog
                    open={!!addDocumentByFolder}
                    folderId={addDocumentByFolder}
                    onOpenChange={() => setAddDocumentByFolder('')}
                />
            )}

            {taskDetailId && (
                <TaskDetailDialog
                    open={!!taskDetailId}
                    onOpenChange={() => setTaskDetailId(null)}
                    taskId={taskDetailId}
                    onNavigateToTask={task => setTaskDetailId(task.id)}
                    hiddenActions
                />
            )}

            <PreviewSearchItem />
            {nodeToPrint && (
                <LabelCustomizationDialog
                    open={!!nodeToPrint}
                    onOpenChange={open => {
                        if (!open) setNodeToPrint(null)
                    }}
                    node={nodeToPrint}
                    projectName={projectDetail?.name || ''}
                    projectCode={projectDetail?.code || ''}
                    projectAreaCode={projectDetail?.area?.code || projectDetail?.region?.code || ''}
                />
            )}
        </>
    )
}
